import type { WSegmentedControlItem } from '@wasabi/actions/WSegmentedControl.types'
import type { MyPrLoop, MyPrLoopChange, PrLoopFix, PrLoopReviews } from './my-pr-loop.types'

export const MY_PR_LOOP_NAME = 'My /pr-loop'

/** "ask" stands for not chosen: the next /pr-loop session asks, as it did before anything was saved. */
export type PrLoopChoice<T extends string> = T | 'ask'

export const PR_LOOP_REVIEW_ITEMS: WSegmentedControlItem[] = [
  { value: 'team', label: "Everyone's PRs" },
  { value: 'mine', label: 'Only mine' },
  { value: 'ask', label: 'Ask me' }
]

export const PR_LOOP_FIX_ITEMS: WSegmentedControlItem[] = [
  { value: 'list', label: 'Mine and the list below' },
  { value: 'mine', label: 'Only mine' },
  { value: 'off', label: 'Off' },
  { value: 'ask', label: 'Ask me' }
]

export interface MyPrLoopDraft {
  reviews: PrLoopChoice<PrLoopReviews>
  fix: PrLoopChoice<PrLoopFix>
  fixFor: string[]
}

export function prLoopDraftFrom(saved: MyPrLoop): MyPrLoopDraft {
  return { reviews: saved.reviews ?? 'ask', fix: saved.fix ?? 'ask', fixFor: [...saved.fixFor] }
}

const sameList = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i])

/** The form after a refetch: a setting the person edited keeps their edit, one they left alone follows what the fleet now holds. */
export function mergePrLoopDraft(old: MyPrLoop, saved: MyPrLoop, draft: MyPrLoopDraft): MyPrLoopDraft {
  const was = prLoopDraftFrom(old)
  const now = prLoopDraftFrom(saved)
  return {
    reviews: draft.reviews === was.reviews ? now.reviews : draft.reviews,
    fix: draft.fix === was.fix ? now.fix : draft.fix,
    fixFor: sameList(draft.fixFor, was.fixFor) ? now.fixFor : draft.fixFor
  }
}

/** Only what the draft changes from what is saved, as PUT /fleet/me/pr-loop takes it; null when nothing changed. */
export function prLoopChangeFrom(saved: MyPrLoop, draft: MyPrLoopDraft): MyPrLoopChange | null {
  const change: MyPrLoopChange = {}
  const reviews = draft.reviews === 'ask' ? null : draft.reviews
  const fix = draft.fix === 'ask' ? null : draft.fix
  if (reviews !== saved.reviews) change.reviews = reviews
  if (fix !== saved.fix) change.fix = fix
  const list = [...new Set(draft.fixFor.map(e => e.trim().toLowerCase()))].sort()
  if (!sameList(list, [...saved.fixFor].sort())) change.fixFor = list
  return Object.keys(change).length ? change : null
}

/** Where the saved answers came from, "Saved on ADX, Nov 2" or "Saved from chris-mbp, Nov 2"; "" while never saved. */
export function prLoopSourceText(saved: Pick<MyPrLoop, 'at' | 'via'>): string {
  if (!saved.at) return ''
  const day = new Date(saved.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return saved.via && saved.via !== 'adx'
    ? `Saved from ${saved.via}, ${day}`
    : `Saved on ADX, ${day}`
}
